import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import { OrgSelector } from './OrgSelector';
import { PermissionGuard, PERMISSIONS } from './PermissionGuard';

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { selectedOrg } = useOrg();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <Link to="/" style={styles.logo}>ZEFNET Enterprise</Link>
          <div style={styles.searchContainer}>
            <input
              type="text"
              placeholder="Search..."
              style={styles.searchInput}
              disabled
            />
          </div>
        </div>
        <div style={styles.headerRight}>
          <OrgSelector />
          <span style={styles.userName}>{user?.name}</span>
          <button onClick={handleLogout} style={styles.logoutButton}>
            Sign Out
          </button>
        </div>
      </header>

      <div style={styles.layout}>
        <nav style={styles.sidebar}>
          <ul style={styles.navList}>
            <li>
              <Link 
                to="/" 
                style={{
                  ...styles.navLink,
                  ...(isActive('/') ? styles.navLinkActive : {})
                }}
              >
                Dashboard
              </Link>
            </li>
            <PermissionGuard permission={PERMISSIONS.ASSETS_READ}>
              <li>
                <Link 
                  to="/assets" 
                  style={{
                    ...styles.navLink,
                    ...(isActive('/assets') ? styles.navLinkActive : {})
                  }}
                >
                  Assets
                </Link>
              </li>
            </PermissionGuard>
            <PermissionGuard permission={PERMISSIONS.REPORTS_READ}>
              <li>
                <Link 
                  to="/compliance/utility-reports" 
                  style={{
                    ...styles.navLink,
                    ...(isActive('/compliance/utility-reports') ? styles.navLinkActive : {})
                  }}
                >
                  Reports
                </Link>
              </li>
            </PermissionGuard>
            <PermissionGuard permission={PERMISSIONS.COMPLIANCE_READ}>
              <li>
                <Link 
                  to="/compliance" 
                  style={{
                    ...styles.navLink,
                    ...(isActive('/compliance') ? styles.navLinkActive : {})
                  }}
                >
                  Compliance
                </Link>
              </li>
            </PermissionGuard>
            <PermissionGuard permission={PERMISSIONS.FINANCE_READ}>
              <li>
                <Link 
                  to="/service/finance" 
                  style={{
                    ...styles.navLink,
                    ...(isActive('/service/finance') ? styles.navLinkActive : {})
                  }}
                >
                  Finance
                </Link>
              </li>
            </PermissionGuard>
            <PermissionGuard permission={PERMISSIONS.ASSETS_READ}>
              <li style={styles.navDivider}>
                <span style={styles.navSection}>Operations</span>
              </li>
              <li>
                <Link 
                  to="/fleet" 
                  style={{
                    ...styles.navLink,
                    ...(isActive('/fleet') ? styles.navLinkActive : {})
                  }}
                >
                  Fleet
                </Link>
              </li>
              <li>
                <Link 
                  to="/uptime" 
                  style={{
                    ...styles.navLink,
                    ...(isActive('/uptime') ? styles.navLinkActive : {})
                  }}
                >
                  Uptime
                </Link>
              </li>
              <li>
                <Link 
                  to="/alerts" 
                  style={{
                    ...styles.navLink,
                    ...(isActive('/alerts') ? styles.navLinkActive : {})
                  }}
                >
                  Alerts
                </Link>
              </li>
              <li>
                <Link 
                  to="/service/work-orders" 
                  style={{
                    ...styles.navLink,
                    ...(isActive('/service/work-orders') ? styles.navLinkActive : {})
                  }}
                >
                  Work Orders
                </Link>
              </li>
            </PermissionGuard>
            <PermissionGuard permission={PERMISSIONS.REPORTS_READ}>
              <li style={styles.navDivider}>
                <span style={styles.navSection}>Data & Reports</span>
              </li>
              <li>
                <Link 
                  to="/data/exports" 
                  style={{
                    ...styles.navLink,
                    ...(isActive('/data/exports') ? styles.navLinkActive : {})
                  }}
                >
                  Exports
                </Link>
              </li>
              <li>
                <Link 
                  to="/data/artifacts" 
                  style={{
                    ...styles.navLink,
                    ...(isActive('/data/artifacts') ? styles.navLinkActive : {})
                  }}
                >
                  Artifacts
                </Link>
              </li>
            </PermissionGuard>
            <PermissionGuard permission={PERMISSIONS.ADMIN}>
              <li style={styles.navDivider}>
                <span style={styles.navSection}>Admin</span>
              </li>
              <li>
                <Link 
                  to="/admin/users" 
                  style={{
                    ...styles.navLink,
                    ...(isActive('/admin/users') ? styles.navLinkActive : {})
                  }}
                >
                  Users
                </Link>
              </li>
              <li>
                <Link 
                  to="/admin/report-profiles" 
                  style={{
                    ...styles.navLink,
                    ...(isActive('/admin/report-profiles') ? styles.navLinkActive : {})
                  }}
                >
                  Report Profiles
                </Link>
              </li>
              <li>
                <Link 
                  to="/admin/templates" 
                  style={{
                    ...styles.navLink,
                    ...(isActive('/admin/templates') ? styles.navLinkActive : {})
                  }}
                >
                  Templates
                </Link>
              </li>
              <li>
                <Link 
                  to="/admin/programs" 
                  style={{
                    ...styles.navLink,
                    ...(isActive('/admin/programs') ? styles.navLinkActive : {})
                  }}
                >
                  Programs
                </Link>
              </li>
              <li>
                <Link 
                  to="/admin/program-packs" 
                  style={{
                    ...styles.navLink,
                    ...(isActive('/admin/program-packs') ? styles.navLinkActive : {})
                  }}
                >
                  Program Packs
                </Link>
              </li>
              <li>
                <Link 
                  to="/admin/plan" 
                  style={{
                    ...styles.navLink,
                    ...(isActive('/admin/plan') ? styles.navLinkActive : {})
                  }}
                >
                  Plan & Entitlements
                </Link>
              </li>
              <li>
                <Link 
                  to="/admin/audit" 
                  style={{
                    ...styles.navLink,
                    ...(isActive('/admin/audit') ? styles.navLinkActive : {})
                  }}
                >
                  Audit Log
                </Link>
              </li>
              <li>
                <Link 
                  to="/admin/config/alert-thresholds" 
                  style={{
                    ...styles.navLink,
                    ...(isActive('/admin/config/alert-thresholds') ? styles.navLinkActive : {})
                  }}
                >
                  Alert Thresholds
                </Link>
              </li>
              <li>
                <Link 
                  to="/admin/config/incident-categories" 
                  style={{
                    ...styles.navLink,
                    ...(isActive('/admin/config/incident-categories') ? styles.navLinkActive : {})
                  }}
                >
                  Incident Categories
                </Link>
              </li>
            </PermissionGuard>
          </ul>
        </nav>

        <main style={styles.main}>
          {children}
        </main>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f7fafc'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 1.5rem',
    background: '#1a365d',
    color: 'white'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center'
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  logo: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: 'white',
    textDecoration: 'none'
  },
  userName: {
    fontSize: '0.875rem'
  },
  logoutButton: {
    padding: '0.5rem 1rem',
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '6px',
    color: 'white',
    cursor: 'pointer',
    fontSize: '0.875rem'
  },
  layout: {
    display: 'flex',
    minHeight: 'calc(100vh - 56px)'
  },
  sidebar: {
    width: '220px',
    background: 'white',
    borderRight: '1px solid #e2e8f0',
    padding: '1rem 0'
  },
  navList: {
    listStyle: 'none',
    padding: 0,
    margin: 0
  },
  navLink: {
    display: 'block',
    padding: '0.75rem 1.5rem',
    color: '#4a5568',
    textDecoration: 'none',
    fontSize: '0.875rem',
    transition: 'background 0.15s, color 0.15s'
  },
  navLinkActive: {
    background: '#edf2f7',
    color: '#1a365d',
    fontWeight: '500',
    borderLeft: '3px solid #3182ce'
  },
  navDivider: {
    marginTop: '1rem',
    paddingTop: '1rem',
    borderTop: '1px solid #e2e8f0'
  },
  navSection: {
    display: 'block',
    padding: '0.5rem 1.5rem',
    fontSize: '0.75rem',
    fontWeight: '600',
    color: '#718096',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  main: {
    flex: 1,
    overflow: 'auto'
  },
  searchContainer: {
    marginLeft: '2rem'
  },
  searchInput: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: 'none',
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    fontSize: '0.875rem',
    width: '200px',
    outline: 'none'
  }
};
